import VueI18n from "vue-i18n";
import directionsMap from "./directions_map";
import arPluralization from "./pluralizations/ar";
import "tinymce";
import "tinymce/themes/silver";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/ui/oxide/content.min.css";
import "tinymce/plugins/lists/plugin";
import "tinymce/plugins/advlist/plugin";
import "tinymce/plugins/paste/plugin";
import "tinymce/plugins/image/plugin";
import "tinymce/plugins/imagetools/plugin";
import "tinymce/plugins/link/plugin";
import "tinymce/plugins/preview/plugin";
import "tinymce/plugins/code/plugin";
import "tinymce/plugins/autoresize/plugin";
import "tinymce/plugins/table/plugin";
import "tinymce/plugins/pagebreak/plugin";
import "tinymce/plugins/charmap/plugin";
import "tinymce/plugins/fullscreen/plugin";
import "tinymce/plugins/anchor/plugin";
import "tinymce/plugins/media/plugin";
import "tinymce/plugins/directionality/plugin";
import "tinymce/icons/default/icons";

// Preload English for the current source
const source = Dashboard.translationSource;
const enMessages = (await import(`@locales/lang/${source}/en.js`)).default;
const loadedLanguages = ['en'];
const alternateStylesheetPath = Dashboard.alternateStylesheetPath;
let useBaseStylesheet = true;
let alternateStylesheetLoaded = false;
let i18n;

export function initializeVuei18n (vue) {
  vue.use(VueI18n);

  i18n = new VueI18n({
    locale: 'en',
    fallbackLocale: 'en',
    messages: enMessages,
    pluralizationRules: {
      'ar': arPluralization
    }
  });

  // Initialize with default locale
  loadLanguageAsync(Dashboard.defaultLocale, true);

  return i18n;
}

// Configure Vite glob imports for all possible languages
const localeFiles = import.meta.glob([
  '@/locales/lang/**/*.js',
  '@/locales/tinymce_locales/*.js'
]);

export async function loadLanguageAsync(lang = 'en', initial = false) {
  if (!initial) {
    loadAlternateStylesheet(lang);
  }

  // Return if already loaded or same language
  if (i18n.locale === lang || loadedLanguages.includes(lang)) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  try {
    // Load main translations
    const messages = (await localeFiles[`/locales/lang/${source}/${lang}.js`]()).default;

    // Load TinyMCE locale if different from English
    if (lang !== 'en') {
      const tinyMCELocale = await localeFiles[`/locales/tinymce_locales/${lang}.js`]();
    }

    i18n.setLocaleMessage(lang, messages[lang]);
    loadedLanguages.push(lang);
    return setI18nLanguage(lang);
  } catch (error) {
    console.error(`Failed to load language ${lang}:`, error);
    return Promise.resolve(setI18nLanguage('en')); // Fallback to English
  }
}

function loadAlternateStylesheet(lang) {
  if (directionsMap[lang] !== directionsMap[i18n.locale]) {
    useBaseStylesheet = !useBaseStylesheet;
    if (alternateStylesheetLoaded) {
      switchStylesheets(lang);
    } else {
      createAlternateStylesheetLink(lang);
    }
  }
}

function switchStylesheets(lang) {
  const baseSheet = document.getElementById('baseStylesheet');
  const altSheet = document.getElementById('alternateStylesheet');

  if (useBaseStylesheet) {
    baseSheet.rel = 'stylesheet';
    altSheet.rel = 'stylesheet alternate';
  } else {
    baseSheet.rel = 'stylesheet alternate';
    altSheet.rel = 'stylesheet';
  }
}

function createAlternateStylesheetLink(lang) {
  const link = document.createElement('link');
  link.href = alternateStylesheetPath;
  link.type = 'text/css';
  link.rel = 'stylesheet';
  link.media = 'all';
  link.id = 'alternateStylesheet';

  document.getElementById('baseStylesheet').after(link);
  link.onload = function() {
    document.getElementById('baseStylesheet').rel = 'stylesheet alternate';
    alternateStylesheetLoaded = true;
  }
}

function setI18nLanguage (lang) {
  i18n.locale = lang;
  document.querySelector("html").setAttribute("lang", lang);
  document.querySelector("html").setAttribute("dir", directionsMap[lang]);
  return lang;
}